import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

import type {
  LeadLostDialogState,
  ParentTypeOption,
} from '@modules/_shared/components/feed/types';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';

type UseLeadLostWorkflowParams = {
  shouldOpen: boolean;
  parentType: ParentTypeOption | null;
  leadId: string | undefined;
  assignedAgentId?: string | null;
  setErrorLike: (error: ErrorLike | null) => void;
};

export const useLeadLostWorkflow = ({
  shouldOpen,
  parentType,
  leadId,
  assignedAgentId,
  setErrorLike,
}: UseLeadLostWorkflowParams) => {
  const [leadLostDialogState, setLeadLostDialogState] =
    useState<LeadLostDialogState | null>(null);
  const [showNoAgentError, setShowNoAgentError] = useState(false);

  const [getLeadAgentStatus] = useLazyQuery(GET_LEAD_AGENT_STATUS, {});

  useEffect(() => {
    if (!shouldOpen) {
      return;
    }

    if (leadLostDialogState != null) {
      return;
    }

    if (parentType !== 'Lead') {
      return;
    }

    if (!leadId) {
      return;
    }

    const fetchLeadAgentForLost = async () => {
      try {
        const result = await getLeadAgentStatus({
          variables: { lead_id: leadId },
        });

        const agents = result.data?.lead_agents ?? [];

        // Only fall back to agents[0] when no assignedAgentId was provided.
        // If assignedAgentId IS set but unmatched, leave selectedAgent null and
        // surface the no-agent error rather than sourcing pipeline/stage from
        // a different agent.
        const selectedAgent =
          assignedAgentId != null
            ? agents.find(agent => agent.user?.id === assignedAgentId) ?? null
            : agents[0] ?? null;

        const effectiveAgentId = selectedAgent?.user?.id ?? null;

        if (effectiveAgentId == null) {
          setShowNoAgentError(true);
          // Don't set dialog state if no agent - prevents showing "No data" dialog
          return;
        }

        const dialogState = {
          leadId,
          pipelineId: selectedAgent?.lead_stage?.pipeline?.id ?? null,
          agentId: effectiveAgentId,
          currentStageId: selectedAgent?.lead_stage?.id ?? null,
        };
        setLeadLostDialogState(dialogState);
      } catch (error) {
        setErrorLike(error as ErrorLike);
        // On error, still show dialog but with null values
        // This allows manual intervention if needed
        setLeadLostDialogState({
          leadId,
          pipelineId: null,
          agentId: assignedAgentId ?? null,
          currentStageId: null,
        });
      }
    };

    void fetchLeadAgentForLost();
  }, [
    shouldOpen,
    getLeadAgentStatus,
    leadId,
    leadLostDialogState,
    parentType,
    assignedAgentId,
    setErrorLike,
  ]);

  useEffect(() => {
    if (!shouldOpen && leadLostDialogState != null) {
      setLeadLostDialogState(null);
      setShowNoAgentError(false);
    }
  }, [shouldOpen, leadLostDialogState]);

  const clearLeadLostDialogState = () => {
    setLeadLostDialogState(null);
    setShowNoAgentError(false);
  };

  const clearNoAgentError = () => setShowNoAgentError(false);

  return {
    leadLostDialogState,
    showNoAgentError,
    clearLeadLostDialogState,
    clearNoAgentError,
  };
};
